import EditIcon from "@mui/icons-material/Edit";
import { Alert, CircularProgress, IconButton } from "@mui/material";
import classNames from "classnames";
import * as React from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useAuth } from "@/providers";
import { PageBreadcrumbs, RobotoTooltip } from "@/shared/components";
import { ExpandableCopyText } from "@/shared/components/ExpandableCopyText";
// Deep import, not the barrel: browser tests stub this module to skip the
// app-shell chrome (sidebar toggle, top-bar notifications) it pulls in.
import { PageLayout } from "@/shared/components/PageLayout";
import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { canEditSkill, SkillVersionRecord } from "@/shared/domain/skills";
import {
  useSkillSummary,
  useSkillVersions,
  useSubscribeSkill,
} from "@/shared/domain/skills/hooks";
import { useAllTopicKeys } from "@/shared/domain/topics/hooks";
import { Button } from "@/shared/foundation/primitives";
import { RobotoNotFoundException } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import styles from "./SkillDetail.module.css";
import { SkillHeader } from "./SkillHeader";
import {
  parseSkillVersionParam,
  skillEditPath,
  SKILL_EDIT_PARAM,
} from "./skillLinks";
import { SkillVersionFields } from "./SkillVersionFields";
import { useSkillVersionForm } from "./skillVersionForm";

type Mode = "view" | "edit";

function latestOf(versions: SkillVersionRecord[]): SkillVersionRecord | null {
  if (versions.length === 0) {
    return null;
  }
  return versions.reduce((a, b) => (a.version > b.version ? a : b));
}

interface VersionRailProps {
  skillId: string;
  versions: SkillVersionRecord[];
  /** Version currently on screen, highlighted in the rail. */
  selected: number | null;
  latest: number | null;
  /** When true, each row also offers a pen straight into that version's editor. */
  canEdit: boolean;
  /** Freeze navigation while an edit is in flight — see the caller. */
  frozen: boolean;
}

/**
 * Every version of the skill, as links.
 *
 * This is what makes the page navigable rather than a viewer for one body: from
 * the skill's own URL, each version is one click from being read *and* one from
 * being edited, without first having to select it and then switch modes. The
 * rows deliberately echo the Skills table's expanded version panel — same
 * ordering, same `v<N>` + description shape, same pen icon — so moving between
 * the list and this page doesn't feel like moving between two products.
 */
function VersionRail({
  skillId,
  versions,
  selected,
  latest,
  canEdit,
  frozen,
}: VersionRailProps) {
  if (versions.length === 0) {
    return null;
  }
  const ordered = [...versions].sort((a, b) => b.version - a.version);
  return (
    <nav className={styles.rail} aria-label="Versions">
      <h2 className={styles.railHeading}>Versions</h2>
      <ul className={styles.railList}>
        {ordered.map((version) => {
          const isSelected = version.version === selected;
          return (
            <li key={version.version}>
              <div
                className={classNames(styles.railRow, {
                  [styles.railRowSelected]: isSelected,
                  [styles.railRowFrozen]: frozen,
                })}
              >
                <Link
                  className={styles.railLink}
                  to={Goto.createSkillRoute(skillId, version.version)}
                  // aria-current marks the row the page is showing, which is
                  // what a screen reader needs to make sense of the highlight.
                  aria-current={isSelected ? "page" : undefined}
                  aria-disabled={frozen || undefined}
                  // A frozen rail stays readable but inert: swapping versions
                  // mid-edit would retarget the pending save.
                  tabIndex={frozen ? -1 : undefined}
                  onClick={(e) => {
                    if (frozen) {
                      e.preventDefault();
                    }
                  }}
                >
                  <span className={styles.railVersion}>
                    v{version.version}
                    {version.version === latest && (
                      <span className={styles.railLatest}>latest</span>
                    )}
                  </span>
                  <span className={styles.railDescription}>
                    {version.description}
                  </span>
                </Link>
                {canEdit && (
                  <RobotoTooltip title={`Edit v${version.version}`}>
                    <span>
                      <IconButton
                        size="small"
                        component={Link}
                        to={skillEditPath(skillId, version.version)}
                        disabled={frozen}
                        aria-label={`Edit v${version.version}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </RobotoTooltip>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * A skill's own page: read any version's body, switch between versions, and —
 * with edit rights — rewrite one in place or publish it as a new version.
 *
 * This is what a share link points at. The id lives in the path
 * (`/agents/skills/:skillId`, optionally `/versions/v:version`), so opening one
 * fetches exactly that skill: no list to page through, no Personal / Org tab to
 * guess at, no filter to seed. Which tab the *list* files a skill under depends
 * on the reader's own relationship to it, and that question simply doesn't
 * arise here.
 *
 * The version shown and the URL stay in lockstep — picking a version navigates
 * — so the copy-link button always hands over what's on screen.
 */
export function SkillDetail() {
  const { skillId, version: versionParam } = useParams<{
    skillId: string;
    version?: string;
  }>();
  const navigate = useNavigate();
  const { currentOrganization, currentUser } = useAuth();
  const orgId = currentOrganization?.org_id;
  const userId = currentUser?.user_id ?? "";

  const summaryQuery = useSkillSummary(orgId, skillId);
  const versionsQuery = useSkillVersions(skillId);
  const subscribeSkill = useSubscribeSkill(orgId);
  const summary = summaryQuery.data ?? null;

  // Edit mode lives in the URL, not in component state: that's what lets the
  // Skills table's per-version pen icon be a plain link into this page rather
  // than a second editing surface. It also means a reload mid-edit comes back
  // to the form, and navigating to another version arrives in whatever mode
  // that URL asks for.
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: Mode =
    searchParams.get(SKILL_EDIT_PARAM) === null ? "view" : "edit";
  const setMode = React.useCallback(
    (next: Mode) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "edit") {
            params.set(SKILL_EDIT_PARAM, "1");
          } else {
            params.delete(SKILL_EDIT_PARAM);
          }
          return params;
        },
        // `replace`: toggling a mode isn't a place to go Back to. Without it,
        // leaving the page would mean clicking Back once per toggle.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const requestedVersion = parseSkillVersionParam(versionParam);
  const versions = React.useMemo(
    () => versionsQuery.data ?? [],
    [versionsQuery.data],
  );
  const latest = latestOf(versions);
  const pinned =
    requestedVersion === null
      ? null
      : versions.find((v) => v.version === requestedVersion) ?? null;
  const chosen = pinned ?? latest;

  // The URL named a version we can't show: either it isn't a version at all
  // (hand-edited path) or it has since been deleted. Say so rather than
  // rendering some other version's body under the version number in the
  // address bar — the reader would have no way to tell they're reading a
  // substitute, and with edit rights they could overwrite it.
  const unresolvedVersion =
    versionParam !== undefined && pinned === null && versionsQuery.isSuccess
      ? versionParam
      : null;

  const canEdit = summary !== null && canEditSkill(summary, userId);
  // The one case where edit rights are a click away rather than absent: an
  // `org-editable` skill grants them to any subscriber, and the reader has no
  // subscription yet. Worth calling out, because nothing else on the page hints
  // at it — the reader sees a skill whose accessibility chip says the org may
  // edit it, and no way to. The Skills table is where subscribing normally
  // happens, and a share link lands here instead.
  const canSubscribeToEdit =
    summary !== null &&
    summary.skill.accessibility === "org-editable" &&
    summary.skill.created_by !== userId &&
    summary.subscription === null;
  // An unresolvable version refuses edit mode outright rather than quietly
  // retargeting. `chosen` falls back to the latest version so there is still
  // something to read, but binding the *form* to that fallback would mean a
  // Save overwrote a body the URL never named — and "Save as new version"
  // published a branch of the wrong parent. Neither is recoverable from the
  // screen, so the page declines to edit anything and says why.
  const isEditing = mode === "edit" && canEdit && unresolvedVersion === null;
  const form = useSkillVersionForm({
    active: isEditing,
    summary,
    version: chosen?.version ?? null,
    orgId,
  });
  // Org topic catalog for styling `[[topic]]` references in the body as known
  // vs. not-yet-known — the same chips the editor shows while authoring. Until
  // it resolves (or if it errors) the body renders without reference chips.
  const topicKeysQuery = useAllTopicKeys(summary?.skill.org_id);
  const knownTopicNames = topicKeysQuery.data?.topic_names;

  async function handleSave() {
    if (await form.save()) {
      setMode("view");
    }
  }

  async function handleSaveAsNewVersion() {
    const created = await form.saveAsNewVersion();
    if (created === null || skillId === undefined) {
      return;
    }
    // Land on what was just published rather than on the version it branched
    // from. Navigating to the bare version URL also drops `?edit=1`, so the
    // page settles on the new body in read mode.
    navigate(Goto.createSkillRoute(skillId, created.version));
  }

  const headerContentLeft = (
    <PageBreadcrumbs>
      <Link to={Goto.route.AgentSkills}>Skills</Link>
      {/* The id, not the name: the name is the page's own editable title, and
          repeating it here would give one field two homes. Matches the dataset
          page. */}
      <ExpandableCopyText text={skillId ?? ""} trimLength={15} />
    </PageBreadcrumbs>
  );
  const pageTitle = `Roboto - ${summary?.skill.name ?? "Skill"}`;

  if (skillId === undefined) {
    // Unreachable through the router (the segment is required to match this
    // route); guards the non-null assertions below for anyone rendering it
    // directly.
    return null;
  }

  if (orgId === undefined || summaryQuery.isLoading) {
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerContentLeft}>
        <div className={styles.page}>
          <CircularProgress size={20} />
        </div>
      </PageLayout>
    );
  }

  if (summaryQuery.isError || summary === null) {
    // Only a genuine 404 means "you can't see this skill". Everything else —
    // a 5xx, a network drop, an endpoint the deployed backend doesn't have yet
    // — is a failure to load, and saying "isn't shared with you" for those
    // sends the reader off chasing a permissions problem that doesn't exist.
    // The server *does* answer "no such skill" and "not shared with you"
    // identically (see `ensure_skill_visible`), so that one message covers both.
    const notVisible = summaryQuery.error instanceof RobotoNotFoundException;
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerContentLeft}>
        <div className={styles.page}>
          {/* The way out lives *inside* the alert. There is nothing else on
              this page — a link floating below the banner reads as unrelated
              page furniture rather than as the answer to "so what now?". */}
          {notVisible ? (
            <Alert severity="info">
              This skill ({skillId}) isn&apos;t in this organization, or
              isn&apos;t shared with you. Ask whoever sent the link to make it
              org-visible, or{" "}
              <Link to={Goto.route.AgentSkills}>go back to all skills</Link>.
            </Alert>
          ) : (
            <Alert severity="error">
              Couldn&apos;t load this skill ({skillId}).{" "}
              {summaryQuery.error?.message ?? "Try reloading the page."}{" "}
              <Link to={Goto.route.AgentSkills}>Back to all skills</Link>.
            </Alert>
          )}
        </div>
      </PageLayout>
    );
  }

  let bodyContent: React.ReactNode;
  if (versionsQuery.isLoading) {
    bodyContent = <CircularProgress size={20} />;
  } else if (versionsQuery.isError) {
    bodyContent = <Alert severity="error">Couldn&apos;t load versions.</Alert>;
  } else if (isEditing) {
    bodyContent = <SkillVersionFields form={form} orgId={orgId} />;
  } else if (chosen) {
    bodyContent = (
      <>
        <div>
          <strong>When to use</strong>
          <p>{chosen.description}</p>
        </div>
        <div className={styles.procedure}>
          <strong>Procedure</strong>
          <div className={styles.body}>
            <RobotoMarkdown
              content={chosen.body}
              variant="compact"
              topicReferences={
                knownTopicNames !== undefined ? { knownTopicNames } : undefined
              }
            />
          </div>
        </div>
      </>
    );
  } else {
    // Defense-in-depth: every skill has ≥1 version as an invariant (deleting
    // the last one cascades into deleting the skill). Reaching this branch
    // implies a future bug, not a live state.
    bodyContent = <p>This skill has no versions.</p>;
  }

  return (
    <PageLayout
      title={pageTitle}
      headerContentLeft={headerContentLeft}
      footer={
        isEditing ? (
          <div className={styles.footerActions}>
            <Button onClick={() => setMode("view")} disabled={form.isMutating}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSaveAsNewVersion()}
              disabled={form.isSaveBlocked}
            >
              Save as new version
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSave()}
              disabled={form.isSaveBlocked}
              loading={form.isMutating}
            >
              Save
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className={styles.page}>
        {unresolvedVersion !== null && (
          <Alert severity="warning">
            This link points at version &ldquo;{unresolvedVersion}&rdquo;, which
            this skill doesn&apos;t have — it may have been deleted since the
            link was shared.
            {chosen !== null && ` Showing v${chosen.version} instead.`}
            {mode === "edit" &&
              canEdit &&
              " Editing is off until you pick a version that exists."}
          </Alert>
        )}

        {canSubscribeToEdit && (
          <Alert
            severity="info"
            action={
              <Button
                size="small"
                onClick={() => subscribeSkill.mutate({ skillId })}
                loading={subscribeSkill.isPending}
              >
                Subscribe
              </Button>
            }
          >
            Anyone in this organization can edit this skill — but only after
            subscribing to it. Subscribing files it under your Personal tab and
            turns on the edit controls here.
          </Alert>
        )}

        <SkillHeader
          summary={summary}
          canEdit={canEdit}
          isAuthor={summary.skill.created_by === userId}
          orgId={orgId}
        />

        <div className={styles.columns}>
          <VersionRail
            skillId={skillId}
            versions={versions}
            selected={chosen?.version ?? null}
            latest={latest?.version ?? null}
            canEdit={canEdit}
            // Switching versions mid-edit would silently retarget the save;
            // Cancel is the way out.
            frozen={isEditing}
          />
          <div className={styles.content}>{bodyContent}</div>
        </div>
      </div>
    </PageLayout>
  );
}
